import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGroupScheduleSetting } from '../../../api/branchScheduleSetting';
import {
  CARD_MIN_OPTIONS,
  DEFAULT_CARD_MIN_MINUTES,
  SNAP_OPTIONS,
  TIME_UNIT_OPTIONS,
} from '../privateLesson/branchScheduleSetting';
import { normalizeWeekStartDay } from '../weekStart';

/**
 * 그룹 스케줄 설정 (그룹 화면 설정 모달에서 저장, 그룹 스케줄 화면이 읽는다). 지점 스케줄 설정과 따로 저장한다.
 * API 가 없거나 실패해도 화면이 그대로 돌도록 항상 기본값으로 채운다 — 1시간 칸·1시간 선택·카드 최소 48분·
 * 지점 공용 캘린더 시간·기본 강의실 없음.
 */
export const GROUP_SCHEDULE_SETTING_DEFAULTS = {
  timeUnitMinutes: 60,
  snapMinutes: 60,
  cardMinMinutes: DEFAULT_CARD_MIN_MINUTES,
  calendarStartTime: null, // null = 지점 공용 캘린더 시간
  calendarEndTime: null,
  defaultRoomId: null,
  // 주 시작 요일 — 응답에 없으면 설정 도입 전 그룹 스케줄 동작(월요일 시작)
  weekStartDay: 'MONDAY',
};

const toHourOrNull = value => {
  if (value == null || value === '') return null;
  const hour = Number(value);
  return Number.isInteger(hour) && hour >= 0 && hour <= 24 ? hour : null;
};

/** 서버 응답(또는 undefined)을 화면이 믿고 쓸 수 있는 값으로 맞춘다 — 빠지거나 이상한 값은 기본값 */
export const normalizeGroupScheduleSetting = data => {
  const defaults = GROUP_SCHEDULE_SETTING_DEFAULTS;
  if (!data || typeof data !== 'object') return defaults;
  const pick = (options, value, fallback) => (options.includes(Number(value)) ? Number(value) : fallback);

  // 시작·종료는 둘 다 있고 종료가 늦을 때만 그룹 전용 시간으로 본다. 아니면 공용 시간
  const start = toHourOrNull(data.calendarStartTime);
  const end = toHourOrNull(data.calendarEndTime);
  const hasOwnCalendar = start != null && end != null && end > start;

  return {
    timeUnitMinutes: pick(TIME_UNIT_OPTIONS, data.timeUnitMinutes, defaults.timeUnitMinutes),
    snapMinutes: pick(SNAP_OPTIONS, data.snapMinutes, defaults.snapMinutes),
    cardMinMinutes: pick(CARD_MIN_OPTIONS, data.cardMinMinutes, defaults.cardMinMinutes),
    calendarStartTime: hasOwnCalendar ? start : null,
    calendarEndTime: hasOwnCalendar ? end : null,
    defaultRoomId: data.defaultRoomId == null || data.defaultRoomId === '' ? null : Number(data.defaultRoomId),
    weekStartDay: normalizeWeekStartDay(data.weekStartDay, defaults.weekStartDay),
  };
};

export const groupScheduleSettingQueryKey = branchId => ['groupScheduleSetting', branchId];

/**
 * 그룹 스케줄 설정 조회. 실패하면 화면은 조용히 기본값으로 돈다.
 * 설정 모달은 기본값을 저장하면 서버 설정을 덮어쓰므로 isLoading(아직 못 받음)·isError 를 함께 돌려준다.
 */
export const useGroupScheduleSetting = (branchId, token) => {
  const { data, isPending, isError } = useQuery({
    queryKey: groupScheduleSettingQueryKey(branchId),
    queryFn: () => getGroupScheduleSetting(branchId, token),
    enabled: !!branchId && !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const setting = useMemo(() => normalizeGroupScheduleSetting(data), [data]);
  return { setting, isLoading: isPending && !isError, isError };
};
